import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const SupportPage = () => {
  const { t } = useTranslation();
  const [activeAccordion, setActiveAccordion] = useState(null);
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  
  const toggleAccordion = (index) => {
    if (activeAccordion === index) {
      setActiveAccordion(null);
    } else {
      setActiveAccordion(index);
    }
  };
  
  // FAQ questions and answers
  const faqItems = [
    {
      question: t('support.faq.questions.accuracy.question'),
      answer: t('support.faq.questions.accuracy.answer')
    },
    {
      question: t('support.faq.questions.bestResults.question'),
      answer: t('support.faq.questions.bestResults.answer')
    },
    {
      question: t('support.faq.questions.security.question'),
      answer: t('support.faq.questions.security.answer')
    },
    {
      question: t('support.faq.questions.cancellation.question'),
      answer: t('support.faq.questions.cancellation.answer')
    },
    {
      question: t('support.faq.questions.frequency.question'),
      answer: t('support.faq.questions.frequency.answer')
    },
    {
      question: t('support.faq.questions.medical.question'),
      answer: t('support.faq.questions.medical.answer')
    },
    {
      question: t('support.faq.questions.faceDataCollection.question'),
      answer: t('support.faq.questions.faceDataCollection.answer')
    },
    {
      question: t('support.faq.questions.faceDataUsage.question'),
      answer: t('support.faq.questions.faceDataUsage.answer')
    },
    {
      question: t('support.faq.questions.faceDataSharing.question'),
      answer: t('support.faq.questions.faceDataSharing.answer')
    },
    {
      question: t('support.faq.questions.faceDataRetention.question'),
      answer: t('support.faq.questions.faceDataRetention.answer')
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          {t('support.title')}
        </PageTitle>
        <PageSubtitle
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
        >
          {t('support.subtitle')}
        </PageSubtitle>
      </PageHeader>

      <ContentContainer>
        <Section
          id="faq"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.2 }}
        >
          <SectionTitle>{t('support.faq.title')}</SectionTitle>
          <AccordionContainer>
            {faqItems.map((item, index) => (
              <AccordionItem key={index}>
                <AccordionHeader 
                  onClick={() => toggleAccordion(index)}
                  isActive={activeAccordion === index}
                >
                  <AccordionQuestion>{item.question}</AccordionQuestion>
                  <AccordionIcon isActive={activeAccordion === index}>+</AccordionIcon>
                </AccordionHeader>
                <AccordionContent
                  initial="collapsed"
                  animate={activeAccordion === index ? "expanded" : "collapsed"}
                  variants={{
                    expanded: { opacity: 1, height: 'auto', marginTop: '10px' },
                    collapsed: { opacity: 0, height: 0, marginTop: '0px' }
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </AccordionContainer>
        </Section>

        <Section
          id="contact"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.3 }}
        >
          <SectionTitle>{t('support.contact.title')}</SectionTitle>
          <ContactContainer>
            <ContactCard
              whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)' }}
            >
              <ContactIcon>✉️</ContactIcon>
              <ContactMethod>{t('support.contact.email')}</ContactMethod>
              <ContactDetail>support@xenya-face.app</ContactDetail>
              <ContactInfo>{t('support.contact.responseTime')}</ContactInfo>
              <ContactButton href="mailto:support@xenya-face.app">{t('support.contact.email')}</ContactButton>
            </ContactCard>
            
            <ContactCard
              whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)' }}
            >
              <ContactIcon>💬</ContactIcon>
              <ContactMethod>{t('support.contact.liveChat')}</ContactMethod>
              <ContactDetail>xenya-face.app/support</ContactDetail>
              <ContactInfo>{t('support.contact.available')}</ContactInfo>
              <ContactButton href="https://xenya-face.app/live-chat" target="_blank">{t('support.contact.liveChat')}</ContactButton>
            </ContactCard>
            
            <ContactCard
              whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)' }}
            >
              <ContactIcon>📞</ContactIcon>
              <ContactMethod>{t('support.contact.phone')}</ContactMethod>
              <ContactDetail>+33 1 23 45 67 89</ContactDetail>
              <ContactInfo>{t('support.contact.available')}</ContactInfo>
              <ContactButton href="tel:+33123456789">{t('support.contact.phone')}</ContactButton>
            </ContactCard>
          </ContactContainer>
        </Section>

        <Section
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.4 }}
        >
          <SupportFormContainer>
            <SupportFormTitle>{t('support.form.title')}</SupportFormTitle>
            <SupportForm>
              <FormGroup>
                <FormLabel>{t('support.form.name')}</FormLabel>
                <FormInput type="text" placeholder={t('support.form.namePlaceholder')} />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>{t('support.form.email')}</FormLabel>
                <FormInput type="email" placeholder={t('support.form.emailPlaceholder')} />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>{t('support.form.subject')}</FormLabel>
                <FormSelect>
                  <option value="">{t('support.form.subjectOptions.default')}</option>
                  <option value="account">{t('support.form.subjectOptions.account')}</option>
                  <option value="subscription">{t('support.form.subjectOptions.subscription')}</option>
                  <option value="technical">{t('support.form.subjectOptions.technical')}</option>
                  <option value="feedback">{t('support.form.subjectOptions.feedback')}</option>
                  <option value="other">{t('support.form.subjectOptions.other')}</option>
                </FormSelect>
              </FormGroup>
              
              <FormGroup>
                <FormLabel>{t('support.form.message')}</FormLabel>
                <FormTextarea placeholder={t('support.form.messagePlaceholder')} rows="5" />
              </FormGroup>
              
              <FormSubmit
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="submit"
              >
                {t('support.contact.submitTicket')}
              </FormSubmit>
            </SupportForm>
          </SupportFormContainer>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.colors.gradientPrimary};
`;

const PageTitle = styled(motion.h1)`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 15px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.gray};
  max-width: 700px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: -30px auto 60px;
  padding: 0 20px;
`;

const Section = styled(motion.section)`
  margin-bottom: 60px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: ${({ theme }) => theme.shadows.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 30px 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 30px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

// FAQ Accordion Styles
const AccordionContainer = styled.div`
  margin-top: 20px;
`;

const AccordionItem = styled.div`
  margin-bottom: 15px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  cursor: pointer;
  background: ${({ isActive, theme }) => 
    isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
  };
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const AccordionQuestion = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  flex: 1;
  color: ${({ theme }) => theme.colors.accent};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const AccordionIcon = styled.span`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-left: 15px;
  transition: ${({ theme }) => theme.transitions.default};
  transform: ${({ isActive }) => isActive ? 'rotate(45deg)' : 'rotate(0)'};
`;

const AccordionContent = styled(motion.div)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  padding: 0 20px;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

// Contact Section Styles
const ContactContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
`;

const ContactCard = styled(motion.div)`
  flex: 1;
  min-width: 250px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 30px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.default};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 100%;
  }
`;

const ContactIcon = styled.div`
  font-size: 40px;
  margin-bottom: 15px;
`;

const ContactMethod = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 5px;
`;

const ContactDetail = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  margin-bottom: 10px;
`;

const ContactInfo = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 20px;
`;

const ContactButton = styled.a`
  display: inline-block;
  background: ${({ theme }) => theme.colors.gradientSecondary};
  color: ${({ theme }) => theme.colors.light};
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

// Support Form Styles
const SupportFormContainer = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.15);
`;

const SupportFormTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 25px;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

const SupportForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  &:nth-child(3), &:nth-child(4) {
    grid-column: span 2;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-column: span 1;
    }
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.md};
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 15px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
  
  option {
    background: ${({ theme }) => theme.colors.secondary};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.md};
  resize: vertical;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FormSubmit = styled(motion.button)`
  grid-column: span 2;
  background: ${({ theme }) => theme.colors.gradientSecondary};
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  margin-top: 10px;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: span 1;
  }
`;

export default SupportPage;